// What? A React component for managing the workspace users.
// Why? To allow the user to manage the users in their workspace.
// How?
// - Fetches the list of users in the workspace using workspaceUserListAPI.
// - Displays the list of users in a table.
// - Allows the user to add new users to the workspace.
// - Allows the user to remove users from the workspace.
// - Allows the user to promote users to different roles.

import React, { useState, useEffect, useCallback } from "react";
import {
  useWorkspaceUserList,
  useWorkspaceUserRemove,
  useWorkspaceUserInviteSend,
  useWorkspaceUserUpdate,
} from "../services/api";
import {
  Box,
  Button,
  Table,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Stack,
  Alert,
  Chip,
  IconButton,
  Sheet,
  Menu,
  MenuItem,
  ListItemDecorator,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { WorkspaceUser } from "../common/types";
import AddUserModal from "../components/Modals/AddUserModal";
import ConfirmationModal from "../components/Modals/ConfirmationModal";
import { useWorkspaceContext } from "../providers/WorkspaceProvider";
import Loading from "../components/Loading";
import PageHeader from "../components/PageHeader";
import Page from "../components/Pages/Page";
import { useMenuOutsideClick } from "../hooks/useMenuOutsideClick";

const WorkspaceUsers = () => {
  const workspaceUserListAPI = useWorkspaceUserList();
  const removeWorkspaceUserApi = useWorkspaceUserRemove();
  const addNewUser = useWorkspaceUserInviteSend();
  const promoteUser = useWorkspaceUserUpdate();

  const { workspace } = useWorkspaceContext();
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [creatingUser, setCreatingUser] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteUserOpen, setDeleteUserOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState<boolean>(false);
  const [promotingUserId, setPromotingUserId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { isOwner, isOnlyOwner } = useWorkspaceContext();

  const title = "Users";

  const [isFetched, setIsFetched] = useState(false);

  const handleAddUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const userEmail = formData.get("userEmail") as string;
    const userRole = formData.get("role") as string;

    if (!workspace?.id) return;

    setCreatingUser(true);
    setError(null);
    try {
      await addNewUser(workspace.id, userEmail, userRole || "user");
      await fetchData();
    } catch (error) {
      console.error("Failed to add user", error);
      setError("Failed to add user. Please try again.");
    } finally {
      setCreatingUser(false);
      setOpen(false);
    }
  };

  const openDeleteUserModal = (userId: string) => {
    setUserToDelete(userId);
    setDeleteUserOpen(true);
    setMenuAnchor(null);
    setSelectedUserId(null);
  };

  const handleDeleteUserConfirmed = async () => {
    if (!workspace?.id) return;

    setIsDeletingUser(true);
    setError(null);
    try {
      if (userToDelete) {
        await removeWorkspaceUserApi(workspace.id, userToDelete);
        await fetchData();
      }
    } catch (error) {
      console.error("Failed to delete user", error);
      setError("Failed to delete user. Please try again.");
    } finally {
      setIsDeletingUser(false);
      setDeleteUserOpen(false);
    }
  };

  const handlePromote = async (role: string, userId: string) => {
    if (!workspace?.id) return;

    setPromotingUserId(userId);
    setError(null);
    try {
      const updatedUser = await promoteUser(workspace.id, role, userId);

      if (updatedUser && updatedUser.role) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.user_id === userId
              ? { ...user, role: updatedUser.role }
              : user,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to promote user", error);
      setError("Failed to promote user. Please try again.");
    } finally {
      setPromotingUserId(null);
      setMenuAnchor(null);
      setSelectedUserId(null);
    }
  };

  const fetchData = useCallback(async () => {
    if (!workspace?.id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await workspaceUserListAPI(workspace.id);
      if (data) {
        setUsers(data);
      }
    } catch (error) {
      console.error("Failed to fetch users", error);
      setError("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, workspaceUserListAPI]);

  useEffect(() => {
    if (workspace?.id && !isFetched) {
      fetchData();
      setIsFetched(true);
    }
  }, [workspace?.id, isFetched, fetchData]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    userId: string,
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUserId(null);
  };

  // Use the reusable hook for outside click handling
  useMenuOutsideClick(menuAnchor, handleMenuClose, menuAnchor);

  const canDemote = (user: WorkspaceUser) => {
    if (isOwner && user.role === "owner" && user.status === "accepted") {
      return !isOnlyOwner;
    } else if (!isOwner && user.role === "owner") {
      return false;
    }
    return true;
  };

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case "accepted":
        return (
          <Chip size="sm" variant="soft" color="success">
            Accepted
          </Chip>
        );
      case "invited":
        return (
          <Chip size="sm" variant="soft" color="warning">
            Invited
          </Chip>
        );
      case "inactivated":
        return (
          <Chip size="sm" variant="soft" color="danger">
            Inactivated
          </Chip>
        );
      default:
        return (
          <Chip size="sm" variant="soft" color="neutral">
            Unknown
          </Chip>
        );
    }
  };

  const getRoleChip = (role: string) => {
    switch (role.toLowerCase()) {
      case "owner":
        return (
          <Chip
            size="sm"
            variant="soft"
            color="danger"
            startDecorator={<AdminPanelSettingsIcon />}
          >
            Owner
          </Chip>
        );
      case "admin":
        return (
          <Chip
            size="sm"
            variant="soft"
            color="primary"
            startDecorator={<AdminPanelSettingsIcon />}
          >
            Admin
          </Chip>
        );
      case "editor":
        return (
          <Chip
            size="sm"
            variant="soft"
            color="warning"
            startDecorator={<EditIcon />}
          >
            Editor
          </Chip>
        );
      case "user":
        return (
          <Chip
            size="sm"
            variant="soft"
            color="neutral"
            startDecorator={<PersonIcon />}
          >
            User
          </Chip>
        );
      default:
        return (
          <Chip size="sm" variant="soft" color="neutral">
            {role}
          </Chip>
        );
    }
  };

  // Component for individual user cards (mobile view)
  const UserCard = ({ user }: { user: WorkspaceUser }) => (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "sm",
          borderColor: "primary.300",
        },
      }}
    >
      <Stack spacing={2}>
        {/* Top row: Email and Role */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              level="title-sm"
              sx={{
                fontWeight: 600,
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {user.email}
            </Typography>
          </Box>
          {promotingUserId === user.user_id ? (
            <CircularProgress size="sm" />
          ) : (
            getRoleChip(user.role)
          )}
        </Stack>

        {/* Bottom row: Status and Action */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>{getStatusChip(user.status)}</Box>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={(e) => handleMenuOpen(e, user.user_id)}
            disabled={!canDemote(user)}
          >
            <MoreVertIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Card>
  );

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">{title}</Typography>
        <Button
          startDecorator={<AddIcon />}
          onClick={() => setOpen(true)}
          variant="solid"
          color="primary"
        >
          Add User
        </Button>
      </PageHeader>

      <Stack spacing={3}>
        {/* Error Display */}
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        {/* Users Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 3 }}
                >
                  <Typography level="h3">Workspace Users</Typography>
                  <Typography level="body-sm" color="neutral">
                    {users.length} user{users.length !== 1 ? "s" : ""} in
                    workspace
                  </Typography>
                </Stack>

                {users.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                      No users in this workspace yet.
                    </Typography>
                    <Button
                      startDecorator={<AddIcon />}
                      onClick={() => setOpen(true)}
                      variant="outlined"
                    >
                      Add your first user
                    </Button>
                  </Box>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <Box
                      sx={{
                        display: { xs: "none", md: "block" },
                        overflow: "auto",
                      }}
                    >
                      <Sheet sx={{ overflow: "auto" }}>
                        <Table>
                          <thead>
                            <tr>
                              <th style={{ width: "40%" }}>User</th>
                              <th style={{ width: "20%" }}>Role</th>
                              <th style={{ width: "20%" }}>Status</th>
                              <th style={{ width: "20%" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user) => (
                              <tr key={user.user_id}>
                                <td>
                                  <Typography level="body-md" fontWeight="md">
                                    {user.email}
                                  </Typography>
                                </td>
                                <td>
                                  {promotingUserId === user.user_id ? (
                                    <CircularProgress size="sm" />
                                  ) : (
                                    getRoleChip(user.role)
                                  )}
                                </td>
                                <td>{getStatusChip(user.status)}</td>
                                <td>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onClick={(e) =>
                                      handleMenuOpen(e, user.user_id)
                                    }
                                    disabled={!canDemote(user)}
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Sheet>
                    </Box>

                    {/* Mobile Card View */}
                    <Box sx={{ display: { xs: "block", md: "none" } }}>
                      <Stack spacing={2}>
                        {users.map((user) => (
                          <UserCard key={user.user_id} user={user} />
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* User Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        placement="bottom-end"
        data-menu="true"
      >
        {selectedUserId &&
          (() => {
            const selectedUser = users.find(
              (u) => u.user_id === selectedUserId,
            );
            if (!selectedUser || !canDemote(selectedUser)) return null;

            // Role hierarchy: higher number = higher access level
            const roleHierarchy: Record<string, number> = {
              user: 1,
              editor: 2,
              admin: 3,
              owner: 4,
            };

            const currentRoleLevel =
              roleHierarchy[selectedUser.role.toLowerCase()] || 0;

            // All available roles
            const roleOptions = [
              {
                role: "admin",
                label: "Admin",
                icon: <AdminPanelSettingsIcon />,
                level: 3,
              },
              { role: "editor", label: "Editor", icon: <EditIcon />, level: 2 },
              { role: "user", label: "User", icon: <PersonIcon />, level: 1 },
            ];

            return (
              <>
                {roleOptions.map((option) => {
                  const isCurrent =
                    option.role === selectedUser.role.toLowerCase();
                  const isPromotion = option.level > currentRoleLevel;
                  const actionLabel = isCurrent
                    ? "Current:"
                    : isPromotion
                      ? "Promote to"
                      : "Demote to";
                  return (
                    <MenuItem
                      key={option.role}
                      onClick={() =>
                        !isCurrent && handlePromote(option.role, selectedUserId)
                      }
                      disabled={isCurrent}
                      sx={isCurrent ? { opacity: 0.5 } : undefined}
                    >
                      <ListItemDecorator>{option.icon}</ListItemDecorator>
                      {actionLabel} {option.label}
                    </MenuItem>
                  );
                })}
                <MenuItem
                  onClick={() => openDeleteUserModal(selectedUserId)}
                  color="danger"
                >
                  <ListItemDecorator>
                    <DeleteIcon />
                  </ListItemDecorator>
                  Remove User
                </MenuItem>
              </>
            );
          })()}
      </Menu>

      {/* Add User Modal */}
      <AddUserModal
        open={open}
        handleAddUser={handleAddUser}
        onClose={() => setOpen(false)}
        buttonDisabled={creatingUser}
        buttonLabel={creatingUser ? <CircularProgress size="sm" /> : "Add"}
      />

      {/* Confirmation Modal for Deleting User */}
      <ConfirmationModal
        open={deleteUserOpen}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        actionLabel="Delete"
        cancelLabel="Cancel"
        onAction={handleDeleteUserConfirmed}
        onClose={() => setDeleteUserOpen(false)}
        loading={isDeletingUser}
        spinner={<CircularProgress size="sm" />}
      />
    </Page>
  );
};

export default WorkspaceUsers;
